// client/src/routes/permisosRuta.js
//
// Mapa ruta -> permiso que exige su ProtectedRoute en AppRoutes.js.
//
// Vive aquí, y no dentro de SmartRedirect, porque lo usan DOS pantallas:
//   - SmartRedirect: para elegir a dónde mandar al usuario al entrar.
//   - Login: para no devolverlo a la ruta donde estaba si su rol no puede verla.
//
// Cuando el mapa estaba sólo dentro de SmartRedirect, Login se lo saltaba al
// volver de una sesión expirada: un rol sin viajes.leer (Monitoreo) que hubiera
// caducado en /dashboard regresaba a /dashboard y rebotaba a /unauthorized en
// cada ingreso.
//
// MANTENER EN SYNC con AppRoutes.js: una ruta con ProtectedRoute que no esté
// aquí queda sin validar, así que Login la trata como no accesible (manda a "/"
// en vez de volver a ella) y SmartRedirect nunca la elige como entrada.

/* El ORDEN importa: SmartRedirect manda a la PRIMERA ruta accesible, así que
 * las de más arriba son las de mayor prioridad como pantalla de entrada.
 * /registroBitacoras va antes que /bitacoras a propósito: el trabajo diario de
 * Monitoreo son los registros de bitácora, no el listado. */
export const RUTAS_PROTEGIDAS = [
  { path: "/viajes", permission: "viajes.leer" },
  { path: "/registroBitacoras", permission: "registros_bitacora.leer" },
  { path: "/bitacoras", permission: "bitacoras.leer" },
  { path: "/choferes", permission: "choferes.leer" },
  { path: "/unidades", permission: "unidades.leer" },
  { path: "/clientes", permission: "clientes.leer" },
  { path: "/rutas", permission: "rutas.leer" },
  { path: "/puntosRuta", permission: "rutas.leer" },
  { path: "/usuarios", permission: "usuarios.leer" },
  { path: "/roles", permission: "configuracion.editar" },
  { path: "/permisos", permission: "configuracion.ver" },

  // Resto de rutas protegidas de AppRoutes. Casi todas comparten viajes.leer
  // con /viajes, que va primero, así que no cambian a dónde aterriza nadie:
  // están para que permisoDeRuta() las reconozca al volver desde Login.
  { path: "/viajes/por-documentacion", permission: "viajes.crear" },
  { path: "/calendario", permission: "viajes.leer" },
  { path: "/calendario/mensual", permission: "viajes.leer" },
  { path: "/certificados", permission: "viajes.leer" },
  { path: "/GastosReales", permission: "viajes.leer" },
  { path: "/analiticas", permission: "viajes.leer" },
  { path: "/casetas", permission: "viajes.leer" },
  { path: "/materiales", permission: "viajes.leer" },
  { path: "/dashboard", permission: "viajes.leer" },
  { path: "/empleados", permission: "choferes.leer" },
  /* Permiso propio del módulo: ya no cuelga de choferes.leer, para poder
   * quitárselo a un rol sin quitarle lo de choferes. */
  { path: "/vacaciones", permission: "vacaciones.leer" },
  /* Alias del módulo anterior: se conserva para que puedeAcceder()
   * reconozca la ruta mientras el redirect a /vacaciones ocurre. */
  { path: "/asistencia", permission: "vacaciones.leer" },
];

const PERMISO_POR_RUTA = new Map(
  RUTAS_PROTEGIDAS.map((r) => [r.path, r.permission]),
);

/* Primera ruta que el usuario puede ver, o null si no puede ver ninguna. */
export const rutaDeEntrada = (hasPermission) =>
  RUTAS_PROTEGIDAS.find((r) => hasPermission(r.permission))?.path ?? null;

/* Permiso que exige una ruta, o null si no está en el mapa. */
export const permisoDeRuta = (path) => PERMISO_POR_RUTA.get(path) ?? null;

/* true sólo si la ruta está en el mapa Y el usuario tiene su permiso.
 * Una ruta desconocida devuelve false a propósito: es preferible mandar al
 * usuario a "/" (donde SmartRedirect decide) que devolverlo a una pantalla que
 * quizá no puede abrir. */
export const puedeAcceder = (path, hasPermission) => {
  const permiso = permisoDeRuta(path);
  return permiso !== null && hasPermission(permiso);
};
